import * as _ from 'lodash';

import { escapeHtml } from '../../../core/services/core.utils.service';
import { looksLikeHtml } from '../../../core/services/markdown-content.functions';

/**
 * Render-time macro expansion for tracker instruction text
 * (`submitInstructions` and `browseInstructions`).
 *
 * Why the read path and not the API: the tracker admin editor loads the same
 * two fields and has to show the *raw* macros. If the API expanded them, saving
 * that screen would persist the expanded text and destroy the macros. Expansion
 * therefore belongs to the components that display the text, never to the
 * transport.
 *
 * Why only a subset of the documented macros: the table in
 * `TrackerItemUtilService::ExpandTemplateString()` is item-scoped — every entry
 * closes over one `$trackerItem` — and it runs only when building notification
 * e-mail bodies. Instructions render on the TI add screen (no item exists yet)
 * and the browse screen (many items), so only macros resolvable from the
 * project / tracker / site context can be expanded here.
 *
 * Unrecognised tokens are deliberately left verbatim rather than blanked, so a
 * mistyped or item-scoped macro stays visible to the admin instead of silently
 * disappearing from the page.
 */

export interface TrackerMacroContext {
    siteName?: string;
    projectName?: string;
    projectUnixName?: string;
    projectUrl?: string;
    trackerName?: string;
    trackerUrl?: string;
}

/** Tokens this expander understands. Also drives the admin help text. */
export const PROJECT_SCOPED_MACROS: string[] = [
    '%SITE_NAME%',
    '%PROJECT_NAME%',
    '%PROJECT_UNIX_NAME%',
    '%PROJECT_URL%',
    '%TRACKER_NAME%',
    '%TRACKER_URL%',
];

/**
 * Matcher for one token, accepting both the plain form an admin types and the
 * markdown-escaped form ToastUI stores — `%SITE_NAME%` and `%SITE\_NAME%` alike.
 *
 * Only `_` needs the allowance: of the characters these tokens are built from
 * (`%`, A-Z, `_`), it is the only one markdown treats as syntax, so it is the
 * only one the serializer escapes.
 */
function tokenPattern(token: string): RegExp {
    const source = token
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')  // regex-escape the literal token
        .split('_')
        .join('\\\\?_');                         // ...then allow an optional `\` before each `_`
    return new RegExp(source, 'g');
}

/** Built once at load: `expandTrackerMacros` runs on every instruction render. */
const MACRO_PATTERNS: { [token: string]: RegExp } = PROJECT_SCOPED_MACROS.reduce(
    (acc: { [token: string]: RegExp }, token: string) => {
        acc[token] = tokenPattern(token);
        return acc;
    },
    {},
);

/**
 * The URL-valued tokens, with the label each should carry once it becomes a
 * link. Every chain ends at the URL itself so a link is never unlabelled.
 */
const URL_MACRO_LABELS: { [token: string]: (context: TrackerMacroContext, url: string) => string } = {
    '%PROJECT_URL%': (context, url) => context.projectName || context.projectUnixName || url,
    '%TRACKER_URL%': (context, url) => context.trackerName || url,
};

/** Backslash-escape the brackets that would otherwise end a link label early. */
function escapeMarkdownLabel(value: string): string {
    return value.replace(/([[\]])/g, '\\$1');
}

/**
 * Renders `url` as a link in the syntax the host document uses (old format support: html, new format: markdown)
 */
function renderLink(label: string, url: string, asHtml: boolean): string {
    return asHtml
        ? `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`
        : `[${escapeMarkdownLabel(label)}](${url})`;
}

const MARKDOWN_DESTINATION = /\]\($/;
const HTML_DESTINATION = /href\s*=\s*["']?$/i;

/**
 * True when the token already sits where a bare URL is expected — `[text](%TOKEN%)`
 * in Markdown, `href="%TOKEN%"` in HTML. An admin who wrote their own link wants
 * their own label, so the raw URL is emitted there rather than a nested link.
 */
function sitsInLinkDestination(precedingText: string, asHtml: boolean): boolean {
    const tail = precedingText.slice(-16);
    return asHtml ? HTML_DESTINATION.test(tail) : MARKDOWN_DESTINATION.test(tail);
}

/**
 * Replaces every project-scoped macro in `template` with its value from
 * `context`. Tokens whose context value is missing are left untouched.
 */
export function expandTrackerMacros(template: string, context: TrackerMacroContext = {}): string {
    if (!template) {
        return '';
    }

    const values: { [token: string]: string } = {
        '%SITE_NAME%': context.siteName,
        '%PROJECT_NAME%': context.projectName,
        '%PROJECT_UNIX_NAME%': context.projectUnixName,
        '%PROJECT_URL%': context.projectUrl,
        '%TRACKER_NAME%': context.trackerName,
        '%TRACKER_URL%': context.trackerUrl,
    };

    const asHtml = looksLikeHtml(template);

    return PROJECT_SCOPED_MACROS.reduce((out: string, token: string) => {
        const value = values[token];
        if (value === undefined || value === null || value === '') {
            return out;
        }
        const labelFor = URL_MACRO_LABELS[token];
        // A replacer *function* rather than a replacement string, so a value
        // containing `$&`, `$1`, ... is inserted literally.
        return out.replace(MACRO_PATTERNS[token], (_match: string, offset: number, whole: string) => {
            if (!labelFor || sitsInLinkDestination(whole.slice(0, offset), asHtml)) {
                return value;
            }
            return renderLink(labelFor(context, value), value, asHtml);
        });
    }, template);
}

/**
 * Builds the context `expandTrackerMacros` reads, from a tracker API object.
 *
 * `hrefFor` is optional: callers without a router (the add modal) omit it, and the
 * two URL macros are then left unexpanded. It must pass `{ inherit: false }`, or
 * `href()` appends the params of whatever state the user is currently on.
 *
 * @example
 * const tracker = {
 *     id: 12, trackerName: 'Bugs', project: { name: 'GForge Next', unixName: 'gforge-next' },
 * };
 * const context = buildTrackerMacroContext(tracker, 'Acme Forge',
 *     (state, params) => this.stateService.href(state, params, { inherit: false }));
 *
 * expandTrackerMacros('Bugs for %PROJECT_NAME% — see %TRACKER_URL%', context);
 * // 'Bugs for GForge Next — see [Bugs](/project/gforge-next/tracker/12/browse)'
 */
export function buildTrackerMacroContext(
    tracker: any,
    siteName: string,
    hrefFor?: (state: string, params: any) => string,
): TrackerMacroContext {
    const unixName = _.get(tracker, 'project.unixName', '');
    const trackerId = _.get(tracker, 'id', '');

    const context: TrackerMacroContext = {
        siteName: siteName,
        projectName: _.get(tracker, 'project.name', ''),
        projectUnixName: unixName,
        trackerName: _.get(tracker, 'trackerName', ''),
    };

    if (hrefFor && unixName) {
        context.projectUrl = hrefFor('home.project.summary', { unixName: unixName });
        if (trackerId !== '') {
            context.trackerUrl = hrefFor('home.project.summary.tracker.browse', {
                unixName: unixName,
                tracker: trackerId,
            });
        }
    }

    return context;
}
